import { Card, Flex } from 'antd';
import React, { FC, useContext, useState } from 'react';
import styled from 'styled-components';
import {
  ClassificationMetadataCancellationTypeEnum,
  FinancialCategory,
  TransactionDirection
} from '@melio/payments-recon-api-client-ts';
import { api } from '@tools/ui/api';
import Loading, { LoadingPosition } from '@tools/ui/components/common/Loading';
import {
  Button,
  IconType,
  semanticTheme,
  TagLabel,
  Typography
} from '@tools/ui/dime';
import useT from '@tools/ui-hooks/useT';
import { convertNumberToCurrencyString } from '@tools/ui-utils/currency.util';
import datesUtil from '@tools/ui-utils/dates.util';
import { RecoConfigContext } from '../../../../components/config-context';
import { financialInstitutionOptions } from '../../../../consts/reco.const';
import { useDDAGaps } from '../../../../hooks/useDDAGaps';
import { useRecoTableQueryState } from '../../../../hooks/useRecoTableQueryState';
import RecoStore from '../../../../stores/recoStore';
import { reconEnumValue } from '../../../../utils/reconEnumValue';
import FilterNavigationMenu from './filter-navigation-menu';

export const SIDEBAR_ANIMATION_DURATION_MS = 250;

const Sidebar = styled(Flex)<{ $isClosing?: boolean }>`
  width: 432px;
  height: 100vh;
  padding: 24px;
  background-color: ${semanticTheme.dime.colors.background.secondary};
  overflow: auto;
  position: relative;
  animation: ${({ $isClosing }) => ($isClosing ? 'slideOut' : 'slideIn')}
    ${SIDEBAR_ANIMATION_DURATION_MS}ms ease-in-out forwards;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(100%);
    }
  }
`;

const Separator = styled.div`
  background-color: #e5e7eb;
  margin: 16px 0;
  height: 1px;
  width: 100%;
`;

const SmallAmount = styled(Typography)`
  margin-top: ${semanticTheme.dime.spacing.s};
`;

interface Props {
  isClosing?: boolean;
}

const DDAClosingStatusSidebar: FC<Props> = ({ isClosing }) => {
  const recoConfig = useContext(RecoConfigContext);
  const { t } = useT();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const { queryState } = useRecoTableQueryState();
  const { ddaGaps, loading, error } = useDDAGaps();

  const accountType = recoConfig.accountTypes
    .filter((accountType) => accountType.value.toLowerCase().includes('dda'))
    .find(
      (accountType) => accountType.value === queryState?.accountType
    )?.label;

  const financialInstitution = financialInstitutionOptions.find(
    (financialInstitution) =>
      financialInstitution.value === queryState?.financialInstitution
  )?.label;

  const fromDate = datesUtil.formatDate(queryState.from, 'MMM D, yyyy');
  const toDate = datesUtil.formatDate(queryState.to, 'MMM D, yyyy');

  if (
    !recoConfig ||
    !accountType ||
    !financialInstitution ||
    !fromDate ||
    !toDate ||
    !ddaGaps ||
    loading ||
    error
  ) {
    return (
      <Sidebar
        $isClosing={isClosing}
        vertical={true}
        gap={semanticTheme.dime.spacing.xl}
      >
        <Flex align="flex-start" justify="space-between">
          <Typography variant="heading3Bold">
            {t('reco.closingStatusSidebar.title')}
          </Typography>

          <Button
            variant="secondary"
            size="middle"
            iconOnly={IconType.Close}
            onClick={() =>
              RecoStore.toggleClosingStatusSidebarVisibility(false)
            }
          />
        </Flex>

        <Loading size="large" position={LoadingPosition.absolute} />
      </Sidebar>
    );
  }

  return (
    <Sidebar
      $isClosing={isClosing}
      vertical={true}
      gap={semanticTheme.dime.spacing.xl}
    >
      <Flex align="flex-start" justify="space-between">
        <Flex vertical={true} gap={semanticTheme.dime.spacing.m}>
          <Typography variant="heading3Bold">
            {t('reco.closingStatusSidebar.title')}
          </Typography>

          <Typography
            variant="body1Bold"
            color={semanticTheme.dime.colors.text.secondary}
          >
            {accountType} &middot; {financialInstitution} &middot; {fromDate} to{' '}
            {toDate}
          </Typography>
        </Flex>

        <Flex gap={semanticTheme.dime.spacing.s}>
          <Button
            variant="secondary"
            size="middle"
            iconOnly={IconType.FileReport}
            onClick={() => {
              setIsGeneratingReport(true);
              api
                .request({
                  method: 'post',
                  url: '/recon/monthly-closing-report',
                  data: {
                    from: queryState.from,
                    to: queryState.to,
                    accountType: queryState.accountType
                  }
                })
                .finally(() => {
                  setIsGeneratingReport(false);
                });
            }}
            loading={isGeneratingReport}
          />

          <Button
            variant="secondary"
            size="middle"
            iconOnly={IconType.Close}
            onClick={() =>
              RecoStore.toggleClosingStatusSidebarVisibility(false)
            }
          />
        </Flex>
      </Flex>

      <Card size="small">
        <Flex
          align="center"
          justify="space-between"
          style={{ marginBottom: semanticTheme.dime.spacing.l }}
        >
          <Typography
            variant="body1Bold"
            color={semanticTheme.dime.colors.text.secondary}
          >
            {t('reco.closingStatusSidebar.revenueStatus')}
          </Typography>

          {ddaGaps.credits.revenueGap === 0 ? (
            <TagLabel variant="positiveSecondary" text="Match" />
          ) : (
            <TagLabel variant="criticalSecondary" text="Unmatch" />
          )}
        </Flex>

        <Flex vertical={true} gap={semanticTheme.dime.spacing.l}>
          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.credits')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                {convertNumberToCurrencyString(ddaGaps.credits.amount)}
              </SmallAmount>
            </div>

            <FilterNavigationMenu
              filters={{
                direction: reconEnumValue<TransactionDirection>('credit')
              }}
            />
          </Flex>

          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.cancellations')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                -{convertNumberToCurrencyString(ddaGaps.credits.cancellations)}
              </SmallAmount>
            </div>

            <FilterNavigationMenu
              filters={{
                cancellationType: [
                  reconEnumValue<ClassificationMetadataCancellationTypeEnum>(
                    'credit'
                  )
                ]
              }}
            />
          </Flex>

          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.netCredits')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                {convertNumberToCurrencyString(ddaGaps.credits.netCredits)}
              </SmallAmount>
            </div>
          </Flex>

          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.revenue')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                {convertNumberToCurrencyString(ddaGaps.credits.revenue)}
              </SmallAmount>
            </div>

            <FilterNavigationMenu
              filters={{
                financialCategory: reconEnumValue<FinancialCategory>('revenue')
              }}
            />
          </Flex>
        </Flex>

        <Separator />

        <Flex vertical={true} gap={semanticTheme.dime.spacing.s}>
          <Typography
            variant="body1"
            color={semanticTheme.dime.colors.text.secondary}
          >
            {t('reco.closingStatusSidebar.revenueGap')}
          </Typography>

          <Typography variant="heading2">
            {convertNumberToCurrencyString(ddaGaps.credits.revenueGap)}
          </Typography>
        </Flex>
      </Card>

      <Card size="small">
        <Flex
          align="center"
          justify="space-between"
          style={{ marginBottom: semanticTheme.dime.spacing.l }}
        >
          <Typography
            variant="body1Bold"
            color={semanticTheme.dime.colors.text.secondary}
          >
            {t('reco.closingStatusSidebar.expensesStatus')}
          </Typography>

          {ddaGaps.debits.expenseGap === 0 ? (
            <TagLabel variant="positiveSecondary" text="Match" />
          ) : (
            <TagLabel variant="criticalSecondary" text="Unmatch" />
          )}
        </Flex>

        <Flex vertical={true} gap={semanticTheme.dime.spacing.l}>
          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.debits')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                {convertNumberToCurrencyString(ddaGaps.debits.amount)}
              </SmallAmount>
            </div>

            <FilterNavigationMenu
              filters={{
                direction: reconEnumValue<TransactionDirection>('debit')
              }}
            />
          </Flex>

          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.cancellations')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                -{convertNumberToCurrencyString(ddaGaps.debits.cancellations)}
              </SmallAmount>
            </div>

            <FilterNavigationMenu
              filters={{
                cancellationType: [
                  reconEnumValue<ClassificationMetadataCancellationTypeEnum>(
                    'debit'
                  )
                ]
              }}
            />
          </Flex>

          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.netDebits')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                {convertNumberToCurrencyString(ddaGaps.debits.netDebits)}
              </SmallAmount>
            </div>
          </Flex>

          <Flex justify="space-between" align="center">
            <div>
              <Typography
                variant="body1"
                color={semanticTheme.dime.colors.text.secondary}
              >
                {t('reco.closingStatusSidebar.expenses')}
              </Typography>

              <SmallAmount variant="heading4Bold">
                {convertNumberToCurrencyString(ddaGaps.debits.expense)}
              </SmallAmount>
            </div>

            <FilterNavigationMenu
              filters={{
                financialCategory: reconEnumValue<FinancialCategory>('expense')
              }}
            />
          </Flex>
        </Flex>

        <Separator />

        <Flex vertical={true} gap={semanticTheme.dime.spacing.s}>
          <Typography
            variant="body1"
            color={semanticTheme.dime.colors.text.secondary}
          >
            {t('reco.closingStatusSidebar.expensesGap')}
          </Typography>

          <Typography variant="heading2">
            {convertNumberToCurrencyString(ddaGaps.debits.expenseGap)}
          </Typography>
        </Flex>
      </Card>
    </Sidebar>
  );
};

export default DDAClosingStatusSidebar;
